import { ProjectIssuanceConstants } from "../../constants/project-issuance";
import { InventoryBreakdownMeasurement } from "../../models";
import { GetAggregateIssuancesResponse } from "../../service/query";

/**
 * Builds the dashboard inventory card's tiles from an `aggregateIssuances` response grouped by
 * `unitMeasurement`. Shared by the Developer and Development Manager dashboards so the two cannot
 * drift apart.
 *
 * `aggregateIssuances` exposes no total-units field (the SQL computes it but it is absent from the
 * schema - KAN-9768 GAP-5), so the per-measurement total is reconstructed as
 * available + reserved + sold, which is how `unitQuantityTotal` itself is defined under BR-02902.
 * That is the same arithmetic the measurement picker used, so the figures are unchanged by the move
 * to tiles.
 */
export const buildInventoryBreakdownMeasurements = (
  results: GetAggregateIssuancesResponse["results"]
): InventoryBreakdownMeasurement[] => {
  const rowTotal = (row: GetAggregateIssuancesResponse["results"][number]): number =>
    Number(row.availableUnits ?? 0) + Number(row.reservedUnits ?? 0) + Number(row.soldUnits ?? 0);

  const measurements = results
    .map((row) => ({ unitMeasurement: row.groupByFieldContent1 ?? "", total: rowTotal(row) }))
    .filter(({ unitMeasurement }) => unitMeasurement !== "");

  /*
    The aggregate carries no ORDER BY for this group-by, so without an explicit sort the tiles could
    reorder between loads. Carbon leads - as it does everywhere else inventory is grouped by
    measurement, and as the picker's own reorder did - and the rest are alphabetical. Do not
    "simplify" this to a plain sort: the Carbon rule is deliberate.
  */
  const CARBON = ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_CARBON;
  return measurements.sort((a, b) => {
    if (a.unitMeasurement === CARBON) return b.unitMeasurement === CARBON ? 0 : -1;
    if (b.unitMeasurement === CARBON) return 1;
    return a.unitMeasurement.localeCompare(b.unitMeasurement);
  });
};
